import {
  extractRoutineStatusTypeValue,
  getExportReference,
  LIST_ASSETS,
  RoutineType,
  SortDirectionEnum,
  useCustomQuery,
  useStatefulWorkspaceRoutines,
} from '@gripp/shared-logic';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { getDateFilter } from './dateFilterUtils';
import { useDataPlateColumns } from './useDataPlateColumns';
import { useExportHook, useExportProps, useExportResult } from './types';

export const useAssetExport: useExportHook = (
  props: useExportProps
): useExportResult => {
  const exportRef = getExportReference(props.workspace.type);
  const referenceColumnKey = exportRef.key;
  const referenceColumnLabel = t(exportRef.labelKey);
  const addAllAssetInfo = !!props.addAllAssetInfo;
  const [rows, setRows] = useState([]);

  const routines = useStatefulWorkspaceRoutines(
    props.workspace.id,
    !props.download
  );

  const dataPlate = useDataPlateColumns(
    props.workspace.id,
    addAllAssetInfo && props.download
  );

  // listAssets columns to request. Routine-state columns are appended per
  // (Counter/Status) routine; description/details only when including asset info.
  const columns = useMemo(() => {
    const cols = [
      referenceColumnKey,
      'lastAddress',
      'lastScannedAt',
      'lastUpdated',
      'reports',
      'notes',
      'alerts',
      'routines',
      'groups',
      'tagCode',
    ];
    if (addAllAssetInfo) {
      cols.push('description', 'details');
    }
    routines.forEach((routine) => cols.push(`routine:${routine.id}`));
    return cols;
  }, [referenceColumnKey, addAllAssetInfo, routines]);

  const headers = useMemo(() => {
    const base = [
      { label: 'ID', key: 'id' },
      { label: 'Name', key: 'name' },
      ...(addAllAssetInfo
        ? [
            { label: 'Description', key: 'description' },
            { label: 'Details', key: 'details' },
          ]
        : []),
      { label: referenceColumnLabel, key: referenceColumnKey },
      { label: 'Last Address', key: 'lastAddress' },
      { label: 'Last Scanned Date', key: 'lastScannedDate' },
      { label: 'Last Scanned Time', key: 'lastScannedTime' },
      { label: 'Last Updated Date', key: 'lastUpdatedDate' },
      { label: 'Last Updated Time', key: 'lastUpdatedTime' },
      { label: '# Notes', key: 'numNotes' },
      { label: '# Alerts', key: 'numAlerts' },
      { label: '# Reports', key: 'numReportsRed' },
      { label: '# Alerts', key: 'numReportsYellow' },
      { label: '# Routines', key: 'numRoutines' },
      { label: 'Groups', key: 'groups' },
      { label: 'Tag Code', key: 'tagCode' },
    ];
    const routineHeaders = routines.map((routine) => ({
      label: routine.config.title ?? routine.name,
      key: `routine:${routine.id}`,
    }));
    return [...base, ...routineHeaders, ...dataPlate.dataPlateHeaders];
  }, [
    addAllAssetInfo,
    referenceColumnLabel,
    referenceColumnKey,
    routines,
    dataPlate.dataPlateHeaders,
  ]);

  const { data: assetsToExport, isLoading } = useCustomQuery({
    query: LIST_ASSETS,
    cacheKey: [
      'exportAssets',
      props.workspace.id,
      props.dateFilter,
      addAllAssetInfo,
    ],
    variables: {
      filter: {
        workspaceId: props.workspace.id,
        columns: columns,
        ...getDateFilter(props.dateFilter),
      },
      orderBy: { field: 'name', order: SortDirectionEnum.Asc },
      page: { limit: 10000, offset: 0 },
    },
    options: {
      enabled: props.download,
      gcTime: 10000,
      staleTime: 10000,
    },
  });

  useEffect(() => {
    if (!assetsToExport) return;
    // Hold off until the data-plate columns are ready so the export isn't
    // triggered with empty "all asset info" columns.
    if (addAllAssetInfo && dataPlate.isLoading) return;

    const exportRows = assetsToExport.asset.listAssets.items.map(
      (item: any) => {
        const row: any = {
          ...(dataPlate.valuesByAssetId[item.id] ?? {}),
        };
        Object.keys(item).forEach((key) => {
          if (key === 'groups') {
            const groups = item[key];
            row[key] = groups
              ? groups.map((group: any) => group.name).join(', ')
              : '';
          } else if (key === 'lastScannedAt' && item[key]) {
            const date = new Date(item[key]);
            row['lastScannedDate'] = date.toISOString().split('T')[0]; // YYYY-MM-DD
            row['lastScannedTime'] = date
              .toISOString()
              .split('T')[1]
              .substring(0, 8); // HH:MM:SS
          } else if (key === 'lastUpdated' && item[key]) {
            const date = new Date(item[key]);
            row['lastUpdatedDate'] = date.toISOString().split('T')[0]; // YYYY-MM-DD
            row['lastUpdatedTime'] = date
              .toISOString()
              .split('T')[1]
              .substring(0, 8); // HH:MM:SS
          } else if (key !== 'routineState') {
            row[key] = item[key];
          }
        });

        if (item.routineState) {
          item.routineState.forEach((routineState: any) => {
            const routine = routines.find(
              (r) => r.id === routineState.workspaceRoutineId
            );
            const state = routineState.state;
            let value: string | undefined = undefined;

            if (routine?.type === RoutineType.Counter) {
              value = state.count;
            } else if (routine?.type === RoutineType.Status) {
              value = extractRoutineStatusTypeValue(routineState);
            }

            if (value !== undefined) {
              row[`routine:${routine?.id}`] = value;
            }
          });
        }

        return row;
      }
    );

    setRows(exportRows);
    props.onDataReady();
  }, [assetsToExport, routines, addAllAssetInfo, dataPlate]);

  return {
    headers,
    rows,
    isLoading: isLoading || (addAllAssetInfo && dataPlate.isLoading),
  };
};
